/**
 * App-Wurzel: EIN aktiver Router (preact-iso). Auth-Seiten (Login/Reset) und die
 * geschützte Tab-Shell werden über den Pfad unterschieden — NICHT über
 * verschachtelte Router (das ließ die Tab-Navigation auf „Heute" hängen).
 */
import { Router, Route, useLocation } from "preact-iso";
import { useEffect } from "preact/hooks";
import { setUnauthorizedHandler } from "../api/client";
import { AuthGuard } from "./AuthGuard";
import { TabBar } from "./TabBar";
import { AppBackground, type BgVariant } from "./AppBackground";
import { CoachSheet } from "../coach/CoachSheet";
import { PromptDialog } from "../coach/PromptDialog";
import { resetCoach } from "../state/coach";
import { installQuerverweisKlicks } from "../content/querverweise";
import { Toast } from "../ui/toast";
import { UpdateBanner } from "./sw-update";
import { CertificateModal } from "../screens/CertificateModal";
import { Login } from "../screens/Login";
import { Reset } from "../screens/Reset";
import { Today } from "../screens/Today";
import { Course } from "../screens/Course";
import { Library } from "../screens/Library";
import { Profile } from "../screens/Profile";
import { Stats } from "../screens/Stats";
import { NotFound } from "../screens/NotFound";

function bgVariant(path: string): BgVariant {
  if (path === "/login" || path.startsWith("/reset")) return "auth";
  if (path.startsWith("/kurs")) return "course";
  return "default";
}

function isAuthPath(path: string): boolean {
  return path === "/login" || path.startsWith("/reset");
}

export function App() {
  const { route, path } = useLocation();
  const auth = isAuthPath(path);

  // 401/403 im Client → Session ist bereits verworfen; Hinweis setzen (wie Web)
  // und zur Login-Seite.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      try {
        sessionStorage.setItem(
          "ki-kurs-login-hinweis",
          "Deine Sitzung wurde beendet, weil eine neue Anmeldung aktiv ist oder die Sitzung abgelaufen ist.",
        );
      } catch {
        /* ignore */
      }
      // Session-Verlust: Coach-Store leeren, sonst bliebe der Verlauf des alten
      // Nutzers in derselben SPA-Sitzung sichtbar (Codex-Hinweis).
      resetCoach();
      route("/login", true);
    });
  }, []);

  // Querverweise im Kurstext ("→ Kapitel 15") intern navigieren statt die PWA
  // zu verlassen. Ein Zuhoerer am Dokument, weil der Lesetext staendig neu
  // eingesetzt wird (siehe content/querverweise.ts).
  useEffect(() => installQuerverweisKlicks(route), [route]);

  return (
    <>
      <AppBackground variant={bgVariant(path)} />
      {auth ? (
        <Router>
          <Route path="/login" component={Login} />
          <Route path="/reset" component={Reset} />
          <Route path="/reset.html" component={Reset} />
          <Route default component={Login} />
        </Router>
      ) : (
        <AuthGuard>
          <div class="app-shell">
            <main class={`app-main${path.startsWith("/kurs") ? " app-main--full" : ""}`}>
              <Router>
                <Route path="/" component={Today} />
                <Route path="/kurs" component={Course} />
                <Route path="/bibliothek" component={Library} />
                <Route path="/profil" component={Profile} />
                <Route path="/statistik" component={Stats} />
                <Route default component={NotFound} />
              </Router>
            </main>
            <TabBar />
            {/* Coach-Overlay-Sheet: über den Coach-Tab (Toggle) erreichbar. */}
            <CoachSheet />
            <PromptDialog />
            <CertificateModal />
            <Toast />
            <UpdateBanner />
          </div>
        </AuthGuard>
      )}
    </>
  );
}
